import createModule from "../createModule"
import view from "./view"
import { Creators } from "./model"
import { Creators as ProfileCreators } from "model/profileModal"
import { Creators as CompositeCreators } from "modules/analyze-composite-security/model"
import { getSelectedGroupValue, getAvailableRollups, getContributorsSummaryText, getCsvData, getGroupBy, getIncludeNotHeld, getSelectedGroupSiblings, getSelectedGroup, getAttributionMethod, getSortedContributorsWithoutCashOffset, getSortedGroupContributorsWithoutCashOffset } from "./selectors"
import { getGroupsDropdownColumns, getDropdownItems } from "./selectors/group"
import { getQueryGroupBy } from "helpers/rollups"
import { generateClassificationQueryForContributors } from "helpers/classification"
import { VIEW_TYPES } from "helpers/base"
import { convertPositionSetType } from "helpers/compositeSecurities"
import { getPage } from "helpers/compositeSecuritiesDetails"

export default createModule({
  view,
  externalProps: (state, props) => ({
    includeNotHeld: getIncludeNotHeld(state, props),
    attributionMethod: getAttributionMethod(state, props),
  }),
  required: ["account", "positionSetId", "positionSetType"],
  data: [{
    resource: "compositeRiskContributors",
    as: null,
    url: ({ positionSetId, positionSetType, modelId, selectedDate, viewType, expand, base, baseSignature, fetchStartDate, segment, classifications, includeNotHeld, attributionMethod }) => {
      if (selectedDate) {
        return {
          id: `compositeRiskContributors-${modelId}${positionSetId}${positionSetType}${fetchStartDate}${selectedDate}${viewType}${expand}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}${attributionMethod}`,
          query: `query (
              $modelId: String!
              $positionSet: PositionSetInput!
              $from: Date!
              $to: Date!
              $base: [PositionSetInput]
              $includeNotHeld: Boolean
              $attributionMethod: RiskContributorAttributionMethod
              ) {
            model (id: $modelId) {
              simulation (positionSet: $positionSet, from: $from, to: $to, base: $base, interval: AUTO) {
                riskContributors (on: $to includeNotHeld: $includeNotHeld, attributionMethod: $attributionMethod) {
                  id
                  assetClass
                  assetSubclass
                  percentEquity
                  notHeld
                  total
                  country
                  ${generateClassificationQueryForContributors("GICS", classifications)}
                  currency
                  description
                  bloombergTicker
                  modelProviderId
                  attribution {
                    summary {
                      factors
                      specific
                    }
                    factors {
                      id
                      name
                      category
                      value
                    }
                  }
                }
              }
            }
          }`,
          variables: {
            modelId,
            positionSet: {
              type: convertPositionSetType(positionSetType),
              id: positionSetId,
              segment,
              expand,
            },
            from: fetchStartDate,
            to: selectedDate,
            base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
            includeNotHeld,
            attributionMethod,
          },
        }
      } else {
        return null
      }
    },
  }, {
    resource: "compositeGroupedRiskContributors",
    as: null,
    url: ({ positionSetId, positionSetType, modelId, selectedDate, viewType, expand, base, baseSignature, fetchStartDate, currentGrouping, segment, classifications, includeNotHeld, attributionMethod }) => {
      if (!currentGrouping) {
        return null
      }

      const queryGroupBy = getQueryGroupBy(currentGrouping, viewType === VIEW_TYPES.ACTIVE)

      return {
        id: `compositeGroupedRiskContributors-${modelId}${positionSetId}${positionSetType}${fetchStartDate}${selectedDate}${JSON.stringify(queryGroupBy)}${viewType}${expand}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}${attributionMethod}`,
        query: `query (
            $modelId: String!
            $positionSet: PositionSetInput!
            $from: Date!
            $to: Date!
            $base: [PositionSetInput]
            $groupBy: ContributorGroupType!
            $groupById: String
            $tier: String
            $includeNotHeld: Boolean
            $attributionMethod: RiskContributorAttributionMethod
          ) {
            model (id: $modelId) {
              simulation (positionSet: $positionSet, from: $from, to: $to, base: $base, interval: AUTO) {
                groupedRiskContributors (on: $to, groupBy: $groupBy, groupById: $groupById, classificationTier: $tier, includeNotHeld: $includeNotHeld, attributionMethod: $attributionMethod) {
                  name
                  id
                  totalPercentEquity
                  attribution {
                    summary {
                      factors
                      specific
                    }
                    factors {
                      id
                      category
                      value
                    }
                  }
                  contributors {
                    id
                    assetClass
                    country
                    notHeld
                    ${generateClassificationQueryForContributors("GICS", classifications)}
                    currency
                    description
                    percentEquity
                    total
                    bloombergTicker
                    modelProviderId
                    attribution {
                      summary {
                        factors
                        specific
                      }
                      factors {
                        id
                        category
                        value
                      }
                    }
                  }
                }
              }
            }
          }`,
        variables: {
          modelId,
          positionSet: {
            type: convertPositionSetType(positionSetType),
            id: positionSetId,
            segment,
            expand,
          },
          from: fetchStartDate,
          to: selectedDate,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          includeNotHeld,
          attributionMethod,
          ...queryGroupBy,
        },
      }
    },
  }],
  mapStateToProps: (state, props) => ({
    data: getSortedContributorsWithoutCashOffset(state, props),
    groupedData: getSortedGroupContributorsWithoutCashOffset(state, props),
    availableRollups: getAvailableRollups(state, props),
    contributorsSummaryText: getContributorsSummaryText(state, props),
    csvData: getCsvData(state, props),
    selectedGroup: getSelectedGroup(state, props),
    selectedGroupValue: getSelectedGroupValue(state, props),
    selectedGroupSiblings: getSelectedGroupSiblings(state, props),
    groupBy: getGroupBy(state, props),
    groupsDropdownColumns: getGroupsDropdownColumns(state, props),
    groupDropdownItems: getDropdownItems(state, props),
    page: getPage(state),
  }),
  actions: {
    ...Creators,
    openSecurityProfile: ProfileCreators.openSecurityProfile,
    openFactorProfile: ProfileCreators.openFactorProfile,
    openCompositeSecurity: CompositeCreators.openCompositeSecurity,
  },
})
